<template>
  <div class="tw:flex tw:flex-col tw:min-w-[320px] tw:w-[320px]">
    <!-- Header with transition -->
    <div class="tw:overflow-hidden tw:h-[40px] tw:relative tw:pb-[18px]">
      <transition name="slide">
        <p :key="currentHeaderVersion" class="tw:absolute tw:font-bold tw:text-center tw:w-full">
          {{ $t(priceData[currentHeaderVersion].bannerHeader) }}
        </p>
      </transition>
    </div>

    <div>
      <!-- Subheader with transition -->
      <div class="tw:bg-white tw:rounded-t-xl tw:border tw:border-gray-300 tw:text-xs">
        <div class="tw:overflow-hidden tw:min-h-[48px] tw:relative tw:p-4">
          <transition name="slide">
            <p
              :key="currentContentVersion"
              class="tw:absolute tw:top-4 tw:left-4 tw:right-4"
              v-html="$t(priceData[currentContentVersion].bannerSubheader)"
            />
          </transition>
        </div>
      </div>

      <!-- Hotel Image -->
      <div
        class="tw:h-[100px] tw:bg-cover tw:bg-center tw:bg-no-repeat"
        :style="{ backgroundImage: `url(${hotelImg})` }"
      />

      <div class="tw:flex tw:flex-col tw:bg-gray-100 tw:p-3 tw:rounded-b-xl tw:gap-y-2">
        <!-- Main Price Card -->
        <div class="tw:border tw:border-green-300 tw:bg-green-100 tw:rounded-xl tw:p-3">
          <div class="tw:overflow-hidden tw:h-[20px] tw:relative tw:mb-1">
            <transition name="slide">
              <div :key="currentContentVersion" class="tw:absolute tw:flex tw:items-center tw:gap-x-1">
                <svg
                  v-if="currentContentVersion === 1"
                  width="15"
                  height="16"
                  viewBox="0 0 15 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M4.73755 7.83921L6.31694 9.41861L9.47573 6.25982M13.4242 7.83921C13.4242 11.3621 9.1125 13.9149 7.61316 14.6961C7.45099 14.7806 7.36991 14.8229 7.25743 14.8448C7.16981 14.8618 7.04346 14.8618 6.95584 14.8448C6.84336 14.8229 6.76228 14.7806 6.60011 14.6961C5.10077 13.9149 0.789062 11.3621 0.789062 7.83921V4.85227C0.789062 4.2209 0.789062 3.90521 0.892324 3.63385C0.983545 3.39412 1.13178 3.18022 1.32421 3.01064C1.54203 2.81867 1.83762 2.70783 2.42879 2.48614L6.66299 0.898313C6.82716 0.836748 6.90925 0.805965 6.9937 0.793762C7.0686 0.782939 7.14467 0.782939 7.21957 0.793762C7.30402 0.805965 7.38611 0.836748 7.55028 0.898313L11.7845 2.48614C12.3757 2.70783 12.6712 2.81867 12.8891 3.01064C13.0815 3.18022 13.2297 3.39412 13.3209 3.63385C13.4242 3.90521 13.4242 4.2209 13.4242 4.85227V7.83921Z"
                    stroke="#1391D2"
                    stroke-width="1.57145"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                <p class="tw:font-bold tw:text-black tw:text-sm">
                  {{ $t(priceData[currentContentVersion].mainProvider) }}
                </p>
              </div>
            </transition>
          </div>

          <div class="tw:flex tw:items-end tw:justify-between">
            <div class="tw:overflow-hidden tw:h-[28px] tw:relative tw:w-[100px]">
              <transition name="slide">
                <p :key="currentContentVersion" class="tw:absolute tw:font-bold tw:text-xl">
                  {{ currentMainPrice }}
                </p>
              </transition>
            </div>
            <div
              class="tw:bg-green-800 tw:rounded-lg tw:text-white tw:flex tw:text-sm tw:px-3 tw:py-[6px] tw:items-center tw:font-bold"
            >
              {{ $t('view_deal_cta') }}
              <ChevronRight class="tw:size-[18px]" />
            </div>
          </div>
        </div>

        <!-- Comparison Prices -->
        <div
          class="tw:flex tw:flex-col tw:bg-white tw:border tw:rounded-xl tw:border-gray-300 tw:px-3 tw:py-2 tw:gap-y-1"
        >
          <!-- First comparison price -->
          <div class="tw:overflow-hidden tw:h-[18px] tw:relative">
            <transition name="slide">
              <p :key="`price-0-${currentContentVersion}`" class="tw:absolute tw:text-xs tw:w-full">
                <span class="tw:font-bold">
                  {{ formatCurrency(currentComparePrices[0].amount, { divideBy100: false }) }}
                </span>
                {{ currentComparePrices[0].partner }}
              </p>
            </transition>
          </div>

          <!-- Second comparison price -->
          <div class="tw:overflow-hidden tw:h-[18px] tw:relative">
            <transition name="slide">
              <p :key="`price-1-${currentContentVersion}`" class="tw:absolute tw:text-xs tw:w-full">
                <span class="tw:font-bold">
                  {{ formatCurrency(currentComparePrices[1].amount, { divideBy100: false }) }}
                </span>
                {{ currentComparePrices[1].partner }}
              </p>
            </transition>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'

import ChevronRight from '@/components/icons/ChevronRight.svg?component'

import { useFormatting } from '@/composables/useFormatting'

const hotelImg = new URL('/src/assets/img/homepage/hotel-bedroom.png', import.meta.url)

const { formatCurrency } = useFormatting()
const currentHeaderVersion = ref(0)
const currentContentVersion = ref(0)
let intervalId = null

const priceData = [
  {
    // Version 1 - Without Rate Connect
    bannerHeader: 'rc_animation_header_no_rc',
    bannerSubheader: 'rc_animation_subheader_no_rc',
    mainProvider: 'Booking.com',
    mainPrice: 159,
    comparePrices: [
      { amount: 160, partner: 'Hotels.com' },
      { amount: 165, partner: 'Expedia' },
    ],
  },
  {
    // Version 2 - With Rate Connect
    bannerHeader: 'rc_animation_header_rc',
    bannerSubheader: 'rc_animation_subheader_rc',
    mainProvider: 'hotel_site',
    mainPrice: 150,
    comparePrices: [
      { amount: 160, partner: 'Booking.com' },
      { amount: 165, partner: 'Expedia' },
    ],
  },
]

const currentMainPrice = computed(() =>
  formatCurrency(priceData[currentContentVersion.value].mainPrice, {
    divideBy100: false,
  })
)

const currentComparePrices = computed(() => priceData[currentContentVersion.value].comparePrices)

const switchContent = () => {
  // First: Change header
  currentHeaderVersion.value = (currentHeaderVersion.value + 1) % 2
  // Second: Content
  setTimeout(() => {
    currentContentVersion.value = (currentContentVersion.value + 1) % 2
  }, 500)
}

onMounted(() => {
  intervalId = setInterval(() => {
    switchContent()
  }, 5000)
})

onBeforeUnmount(() => {
  if (intervalId) clearInterval(intervalId)
})
</script>

<style scoped>
.slide-enter-active,
.slide-leave-active {
  transition: all 0.5s ease;
}

.slide-enter-from {
  transform: translateY(-100%);
  opacity: 0;
}

.slide-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

.slide-enter-to,
.slide-leave-from {
  transform: translateY(0);
  opacity: 1;
}
</style>
